import { Divider, Text, Loader } from '@mantine/core'
import { useForm } from '@mantine/form'
import { Check, X } from 'lucide-react'
import { zodResolver } from 'mantine-form-zod-resolver'

import { useMutation } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import DnDrawer from 'src/components/Core/DnDrawer'
import {
  FormTextField,
  FormMonthYearField,
} from 'src/components/Overlays/EmploymentDetailsDrawer/components'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  GET_APPLICATION,
  GET_APPLICATION_BY_APPLICANT_ID,
} from 'src/graphql/application.graphql'
import {
  CREATE_EMPLOYMENT_EXPERIENCE,
  GET_EMPLOYMENT_EXPERIENCES_BY_APPLICATION_ID,
} from 'src/graphql/employmentExperiences.graphql'
import {
  UnemploymentExperienceSchema,
  UnemploymentExperienceFormData,
} from 'src/validations'

interface AddUnemploymentDrawerProps {
  opened: boolean
  close: () => void
  applicationId: string
  applicantId?: string
  initialStartDate?: Date
  initialEndDate?: Date
}

const AddUnemploymentDrawer = ({
  opened,
  close,
  applicationId,
  applicantId,
  initialStartDate,
  initialEndDate,
}: AddUnemploymentDrawerProps) => {
  const getInitialValues = () => {
    return {
      startDate: initialStartDate || new Date(),
      endDate: initialEndDate || new Date(),
      unemploymentReason: '',
    }
  }

  const form = useForm({
    initialValues: getInitialValues(),
    validate: zodResolver(UnemploymentExperienceSchema),
  })

  // Create employment experience mutation for unemployment
  const [createEmploymentExperience, { loading }] = useMutation(
    CREATE_EMPLOYMENT_EXPERIENCE,
    {
      refetchQueries: [
        {
          query: GET_EMPLOYMENT_EXPERIENCES_BY_APPLICATION_ID,
          variables: { applicationId },
        },
        {
          query: GET_APPLICATION,
          variables: { id: applicationId },
        },
        ...(applicantId
          ? [
              {
                query: GET_APPLICATION_BY_APPLICANT_ID,
                variables: { applicantId },
              },
            ]
          : []),
      ],
      awaitRefetchQueries: true,
      onCompleted: () => {
        toast('Unemployment period created successfully', 'success')
        close()
      },
      onError: (error) => {
        toast('Failed to create unemployment period', 'error', {
          error: error,
        })
      },
    }
  )

  const handleSubmit = () => {
    const validation = form.validate()
    if (validation.hasErrors) {
      return
    }

    const values = form.values
    // Type the values properly
    const typedValues = values as UnemploymentExperienceFormData

    const input = {
      companyName: 'n/a',
      position: 'n/a',
      startDate: typedValues.startDate,
      endDate: typedValues.endDate,
      reasonForLeaving: 'n/a',
      unemploymentReason: typedValues.unemploymentReason?.trim(),
      dotNumber: null,
      contactName: null,
      email: null,
      phone: null,
      faxNumber: null,
      equipmentTypes: [],
      isDriverTerminated: false,
      isDriverSubjectToFMCSRs: true,
      isJobDesignatedAsSafetySensitiveFunctionUnderDOT: true,
      shouldContactEmployer: true,
      isUnemployment: true,
      // Don't include address for unemployment periods
    }

    createEmploymentExperience({
      variables: {
        applicationId,
        input,
      },
    })
  }

  const handleCancel = () => {
    form.reset()
    close()
  }

  return (
    <DnDrawer opened={opened} onClose={close}>
      <div className="flex h-screen flex-col space-y-4 pb-14">
        {/* HEADER */}
        <div className="flex flex-row items-center justify-between">
          <Text size="lg" fw={500}>
            Add Unemployment Period
          </Text>
          <div className="flex items-center gap-2">
            <DNButton
              variant="transparent"
              onClick={handleSubmit}
              disabled={loading}
              className="text-doubleNickel-success-500"
            >
              <Check size={14} />
              Save
            </DNButton>
            <DNButton
              variant="transparent"
              onClick={handleCancel}
              disabled={loading}
              className="text-doubleNickel-error-500"
            >
              <X size={14} />
              Cancel
            </DNButton>
            {loading && <Loader size={12} color="gray" />}
          </div>
        </div>
        <Divider variant="dashed" />

        {/* BODY */}
        <div className="flex h-screen flex-col space-y-3 overflow-auto">
          <Text size="xs" fw={400} className="text-doubleNickel-gray-500">
            UNEMPLOYMENT DETAILS
          </Text>
          <FormMonthYearField
            label="Start Date"
            required={true}
            editing={true}
            formProps={{
              ...form.getInputProps('startDate'),
              maxDate: form.values.endDate
                ? form.values.endDate instanceof Date
                  ? form.values.endDate
                  : new Date(form.values.endDate as string | number)
                : new Date(),
            }}
          />
          <FormMonthYearField
            label="End Date"
            editing={true}
            formProps={{
              ...form.getInputProps('endDate'),
              minDate: form.values.startDate
                ? form.values.startDate instanceof Date
                  ? form.values.startDate
                  : new Date(form.values.startDate as string | number)
                : undefined,
              maxDate: new Date(),
              disabled: !form.values.endDate,
            }}
          />
          <FormTextField
            label="Unemployment Reason"
            required={true}
            editing={true}
            formProps={{
              ...form.getInputProps('unemploymentReason'),
              placeholder: 'Enter unemployment reason',
            }}
          />
        </div>
      </div>
    </DnDrawer>
  )
}

export default AddUnemploymentDrawer
